import {Injectable, Injector, NgZone} from '@angular/core';
import {filter, map, take, tap} from 'rxjs/operators';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {Store} from '@ngrx/store';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import {AuthenticationService} from "@ispm/user/services/authentication.service";

@Injectable()
export class SessionRefreshService {
  private logoutTimer = -1;
  private refreshTokenTimer = -1;
  private isSSOLogin = false;
  private logoutTimerInSeconds = 30 * 60;
  private sessionExpirationTimeInSeconds = 30 * 60;
  private refreshBeforeSessionExpiredInSeconds = 900;
  private ssoConfiguration: any;
  private readonly excludeFromApiCalls = ['/auth/refresh', '/hb'];
  private readonly SSO_IFRAME_ID = 'ssoIframe';
  public lastUserInteractionKey: string = 'lastUserInteraction';
  public readonly LAST_USER_INTERACTION_DEFAULT: string = 'lastUserInteraction';
  private renewSSOIsTriggered: boolean = false;
  private _isNewSession: boolean = false;

  constructor(protected injector: Injector, protected coreAC: CoreActionCreatorService, private store: Store<IgenomicsState>,
              private zone: NgZone) {
    this.waitForConfiguration();
  }

  private waitForConfiguration(): void {
    this.coreAC.skipUserInteractionUpdate(true);
    Observable.forkJoin({
      organizationConfiguration: this.store.select('users', 'organizationConfiguration').pipe(filter(organizationConfiguration => !!organizationConfiguration),take(1)),
      environmentDetails: this.store.select('core', 'environmentDetails').pipe(filter(environmentDetails => !!environmentDetails),take(1)),
      authService: this.authService}).pipe(take(1))
      .subscribe((sessionRefreshConfigurations) => {
        const organizationConfiguration = sessionRefreshConfigurations.organizationConfiguration;
        const sessionConfiguration = organizationConfiguration.sessionConfiguration;
        const environmentDetails = sessionRefreshConfigurations.environmentDetails;
        const user = sessionRefreshConfigurations.authService.user;
        this.ssoConfiguration = organizationConfiguration.ssoConfiguration;
        if (sessionConfiguration) {
          if (sessionConfiguration.refreshBeforeSessionEndTime) {
            this.refreshBeforeSessionExpiredInSeconds = sessionConfiguration.refreshBeforeSessionEndTime;
          }
          if (sessionConfiguration.sessionTime) {
            this.logoutTimerInSeconds = sessionConfiguration.sessionTime;
          }
        }
        if (environmentDetails.domain) {
          this.lastUserInteractionKey = this.lastUserInteractionKey + '_' + environmentDetails.domain;
        }
        if (environmentDetails.identifier) {
          this.lastUserInteractionKey = this.lastUserInteractionKey + '_' + environmentDetails.identifier;
        }
        //check if the user is logged in then need to clear lastUserInteraction from last session
        if (this.isNewSession && localStorage.getItem(this.lastUserInteractionKey)) {
          localStorage.removeItem(this.lastUserInteractionKey);
        }
        this.startService(user);
      });
  }

  get isNewSession(): boolean {
    return this._isNewSession;
  }

  set isNewSession(value: boolean) {
    this._isNewSession = value;
  }

  public apiCalled(url: string) {
    this.store.select('core', 'skipUserInteractionUpdate').pipe(
      filter(skipUserInteractionUpdate => !skipUserInteractionUpdate)
    ).subscribe(() => {
      if (!(url && this.allowRestartLogoutTimer(url))) {
        return;
      }
      this.restartLogoutTimer(url);
    });
  }

  public setIsSSOLogin(): void {
    this.isSSOLogin = true;
  }

  //this method must be called only at constructor
  private startService(user): void {
    if (!user) {
      return null;
    }
    if(!this.renewSSOIsTriggered && this.lastUserInteractionKey !== this.LAST_USER_INTERACTION_DEFAULT){
      //it means the user performs refresh browser, need to calculate sso session time from last user interaction time
      this.sessionExpirationTimeInSeconds = user.exp - (this.getTimeSinceUserInteractionInMilliseconds() / 1000);
    } else {
      this.sessionExpirationTimeInSeconds = user.exp;
    }
    this.coreAC.skipUserInteractionUpdate(false);
    this.startSessionTimers();
  }

  public stopService() {
    window.clearInterval(this.refreshTokenTimer);
    window.clearTimeout(this.logoutTimer);
  }

  private allowRestartLogoutTimer(url): boolean {
    for (const excludedUrl of this.excludeFromApiCalls) {
      if (url.indexOf(excludedUrl) !== -1) {
        return false;
      }
    }
    return true;
  }

  private async restartLogoutTimer(url?,isRestartLogoutTimer? : boolean) {
    const authService = (await this.authService);
    if (!authService.user) {
      return;
    }
    if (!isRestartLogoutTimer) {
      this.store.select('core', 'skipUserInteractionUpdate').pipe(
        filter(skipUserInteractionUpdate => !skipUserInteractionUpdate)
      ).subscribe(() => {
        if (this.lastUserInteractionKey !== this.LAST_USER_INTERACTION_DEFAULT && !this.renewSSOIsTriggered) {
          localStorage.setItem(this.lastUserInteractionKey, Date.now().toString());
        }
      });
    }

    window.clearTimeout(this.logoutTimer);
    this.zone.runOutsideAngular(() => {
      this.logoutTimer = window.setTimeout(async () => {
        if (this.getTimeSinceUserInteractionInMilliseconds() / 1000 >= this.logoutTimerInSeconds) {
          // session is old - token is already expired. perform logout
          window.clearInterval(this.refreshTokenTimer);
          authService.logout({refresh: true, callFromResponseHandler: true,fromClient: true});
          return;
        } else {
          return this.restartLogoutTimer(url, true);
        }
      }, 1000 * this.logoutTimerInSeconds - this.getTimeSinceUserInteractionInMilliseconds());
    });
  }

  private getTimeSinceUserInteractionInMilliseconds() {
    return localStorage.getItem(this.lastUserInteractionKey) ? Date.now() - parseInt(localStorage.getItem(this.lastUserInteractionKey)) : 0;
  }

  get authService() {
    return this.coreAC.getUpgradeInjectorStatus().pipe(
      filter(status => status),
      take(1),
      map(_ => this.injector.get(AuthenticationService))
    ).toPromise();
  }

  private startSessionTimers(): void {
    this.startRefreshTimers();
    this.restartLogoutTimer();
  }

  private startRefreshTimers(): void {
    let refreshTokenTimer = this.sessionExpirationTimeInSeconds - this.refreshBeforeSessionExpiredInSeconds;

    refreshTokenTimer = Math.max(0, refreshTokenTimer);
    if (isNaN(refreshTokenTimer) || refreshTokenTimer === 0) {
      //to protect the system from crashing
      window.clearInterval(this.refreshTokenTimer);
      this.renewSession();
    } else {
      this.zone.runOutsideAngular(() => {
        window.clearInterval(this.refreshTokenTimer);
        this.refreshTokenTimer = window.setInterval(async () => {
          this.renewSession();
        }, refreshTokenTimer * 1000);
      });
    }
  }

  private async renewSession() {
    if (this.isSSOLogin) {
      if (this.ssoConfiguration && this.ssoConfiguration.samlRequestUrl)
        this.renewSSOToken();
    } else {
      await this.refreshHSDPSession();
    }
  }

  private async refreshHSDPSession() {
    const authService = (await this.authService);
    authService.refresh({fromClient: true});
  }

  private async removeSSOiframe(e) {
    const iframe = document.getElementById(this.SSO_IFRAME_ID);
    iframe.parentNode.removeChild(iframe);
  }

  private createSSOIframe(): void {
    let iframe: HTMLElement;
    iframe = document.createElement('iframe');
    const url = this.ssoConfiguration.samlRequestUrl;
    iframe.setAttribute('src', url);
    iframe.id = this.SSO_IFRAME_ID;
    document.body.appendChild(iframe);
  }

  private renewSSOToken(): void {
    this.createSSOIframe();

    window.addEventListener('message', async (event) => {
      let iframe = document.getElementById(this.SSO_IFRAME_ID);
      if (iframe && iframe['contentWindow'] == event.source) {
        this.renewSSOIsTriggered = true;
        (await this.authService).setCurrentUser(event.data,true);
        await this.removeSSOiframe(event);
        await this.coreAC.skipUserInteractionUpdate(false);
        this.renewSSOIsTriggered = false;
      }
    });
  }
}
